
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, Save, X } from 'lucide-react';

interface AccountSettingsProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Account Settings</CardTitle>
          <CardDescription>Manage your personal account settings and preferences</CardDescription>
        </div>
        <div className="flex space-x-2">
          {editing ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onSave}
                className="flex items-center gap-1"
              >
                <Save className="h-4 w-4" />
                Save
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onEditToggle}
                className="flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onEditToggle}
              className="flex items-center gap-1"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" defaultValue="John Doe" readOnly={!editing} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" defaultValue="john.doe@example.com" readOnly={!editing} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input id="role" defaultValue="System Administrator" readOnly={!editing} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Input id="department" defaultValue="IT Operations" readOnly={!editing} />
            </div>
          </div>
          
          <div className="pt-4 border-t">
            <h3 className="text-lg font-medium mb-4">Security</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Two-Factor Authentication</Label>
                  <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                </div>
                <Switch disabled={!editing} defaultChecked={true} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">API Access</Label>
                  <p className="text-sm text-muted-foreground">Allow API access to your account</p>
                </div>
                <Switch disabled={!editing} defaultChecked={false} />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountSettings;
